import { round } from 'lodash';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import range from 'lodash/range';
// eslint-disable-next-line import/no-unresolved
import { Layout, PlotData } from 'plotly.js-basic-dist';
import { COMPANY_MODEL, CreMetric, HistoricalRecord, RiskProductsData } from '../../../../models/RiskProducts';
import {
  computeYearsAxisData,
  filterRiskMetricsByYearRange,
  mapRiskMetricsToYearsAxisValues,
  sortMetricsDescByCalculationDate,
} from '../RiskProductsWidget.logic';
import {
  EUROPE_REGION,
  FINANCIAL_EURO_CRL,
  FINANCIAL_NON_EURO_CRL,
  NON_FINANCIAL_CRL,
} from './CreChart.logic.constants';
import { plotDefaultData, plotDefaultLayout } from './CreChart.presentational.constants';
import { PlotYAxisData, Tick } from './CreChart.types';

export const hasCreMetrics = (creMetrics: CreMetric[]): boolean => {
  const creValues = creMetrics.map((metric) => metric.crePercentage).filter((cre) => cre !== null);
  return !(isEmpty(creMetrics) || isEmpty(creValues));
};

// ------------ Filter cre metrics ------------
export const getCreMetrics = (riskMetrics: RiskProductsData): CreMetric[] => {
  const creMetrics = riskMetrics?.data.length > 0 ? processCreMetrics(riskMetrics.data) : [];
  return sortMetricsDescByCalculationDate(creMetrics);
};

export const processCreMetrics = (riskMetrics: HistoricalRecord[]): CreMetric[] => {
  const filteredRiskMetrics = filterRiskMetricsByYearRange(riskMetrics);
  return getValidCreMetrics(filteredRiskMetrics);
};

const getValidCreMetrics = (allCreMetrics: HistoricalRecord[]): CreMetric[] => {
  const expectedCreMetrics: CreMetric[] = [];
  allCreMetrics.forEach((metric) => {
    const creValue = metric.comulative_default_probalitity_1y;
    if (creValue) {
      const crePercentage = creValue * 100;
      const creValid = crePercentage <= 100 && crePercentage > 0;
      if (creValid) {
        const precision = getPrecision(crePercentage);
        expectedCreMetrics.push({
          crePercentage: round(crePercentage, precision),
          creditRiskLevel: metric.credit_risk_level || '',
          calculationDate: metric.calculation_date,
        });
      }
    }
  });
  return expectedCreMetrics;
};

const getPrecision = (value: number) => {
  if (value < 0.01) {
    return 5;
  }

  if (value >= 0.01 && value < 0.1) {
    return 3;
  }

  return 2;
};

// ------------ Update Plot ------------

export const composePlotParams = (
  filteredCreMetrics: CreMetric[],
  companyModelId: string,
  companyRegionId: string,
  plotData?: Partial<PlotData>[] | null,
  plotLayout?: Partial<Layout>
): [Partial<PlotData>[], Partial<Layout>] => {
  const updatedPlotData: Partial<PlotData>[] = plotData
    ? cloneDeep<Partial<PlotData>[]>(plotData)
    : cloneDeep<Partial<PlotData>[]>(plotDefaultData);
  const updatedPlotLayout: Partial<Layout> = plotLayout
    ? cloneDeep<Partial<Layout>>({ ...plotDefaultLayout, ...plotLayout })
    : cloneDeep<Partial<Layout>>(plotDefaultLayout);

  const creAxisValues = filteredCreMetrics.map((metric) => metric.crePercentage);
  const yearAxisValues = mapRiskMetricsToYearsAxisValues(filteredCreMetrics);
  updatedPlotData[0].x = yearAxisValues;
  updatedPlotData[0].y = creAxisValues;

  updatedPlotData[1].x = yearAxisValues;
  updatedPlotData[1].y = [];

  if (!updatedPlotLayout.xaxis?.tickvals) {
    updatedPlotLayout.xaxis = {
      ...updatedPlotLayout.xaxis,
      ...computeYearsAxisData(filteredCreMetrics[0].calculationDate),
    };
  }

  const yaxisData = computeYAxisData(companyModelId, companyRegionId, creAxisValues);

  updatedPlotLayout.yaxis = {
    ...updatedPlotLayout.yaxis,
    ...yaxisData.cre,
  };
  updatedPlotLayout.yaxis2 = {
    ...updatedPlotLayout.yaxis2,
    ...yaxisData.crl,
  };

  return [updatedPlotData, updatedPlotLayout];
};

const computeYAxisData = (companyModelId: string, companyRegionId: string, creAxisValues: number[]): PlotYAxisData => {
  // 1 percent is always shown in Y-axis
  const minYAxisValue = Math.log10(Math.min(...creAxisValues, 1));
  const maxYAxisValue = Math.log10(Math.max(...creAxisValues, 1));
  const yAxisLowestRange = Math.floor(minYAxisValue);
  const yAxisHighestRange = Math.ceil(maxYAxisValue);
  // 0.1 is added or removed from range value to show lowest and highest chart lines
  const yAxisRange = [yAxisLowestRange - 0.1, yAxisHighestRange + 0.1];

  const logTicks: number[] = getLogarithmicTickValues(yAxisLowestRange, yAxisHighestRange);
  const crlTicks = getCompanyModelTicks(companyModelId, companyRegionId, yAxisLowestRange, yAxisHighestRange);
  const crlTickValues = crlTicks.map((tick) => tick.key);

  const combinedCreTickValues: Set<number> = new Set([...logTicks, ...crlTickValues]);
  const creTickVals: number[] = Array.from(combinedCreTickValues).sort((a, b) => a - b);
  const creTickText: string[] = Array.from(creTickVals).map((value) =>
    value.toFixed(Math.max(2, (value.toString().split('.')[1] || []).length))
  );
  const crlTickText: string[] = crlTicks.map((tick) => tick.text);

  return {
    cre: {
      tickvals: creTickVals,
      ticktext: creTickText,
      range: yAxisRange,
    },
    crl: {
      tickvals: crlTickValues,
      ticktext: crlTickText,
      range: yAxisRange,
    },
  };
};

const getLogarithmicTickValues = (lowestRangeValue: number, highestRangeValue: number) =>
  range(lowestRangeValue, highestRangeValue + 1, 1).map((key) => Number((10 ** key).toFixed(Math.abs(key))));

const getCompanyModelTicks = (
  companyModelId: string,
  companyRegionId: string,
  lowestRangeValue: number,
  highestRangeValue: number
) => {
  const yAxisCompanyModelTicks: Tick[] = getYAxisTicksByCompany(companyModelId, companyRegionId);
  return yAxisCompanyModelTicks.filter((tick) => {
    const baseValue = Math.log10(tick.key);
    return baseValue >= lowestRangeValue && baseValue < highestRangeValue;
  });
};

const getYAxisTicksByCompany = (companyModelId: string, companyRegionId: string): Tick[] => {
  if (companyModelId === COMPANY_MODEL.BANKS && companyRegionId === EUROPE_REGION) {
    return FINANCIAL_EURO_CRL;
  }

  switch (companyModelId) {
    case COMPANY_MODEL.AEROSPACE:
    case COMPANY_MODEL.AIRLINES:
    case COMPANY_MODEL.AUTOMOTIVE:
    case COMPANY_MODEL.CAPITAL_GOODS:
    case COMPANY_MODEL.CHEMICALS:
    case COMPANY_MODEL.CONSTRUCTION_AGGREGATES:
    case COMPANY_MODEL.CONSUMER:
    case COMPANY_MODEL.DIVERSIFIED_CONGLOMERATES:
    case COMPANY_MODEL.FOOD_BEVERAGES:
    case COMPANY_MODEL.GAMING_LEISURE:
    case COMPANY_MODEL.HEALTHCARE_PHARMACEUTICALS:
    case COMPANY_MODEL.HOMEBUILDING:
    case COMPANY_MODEL.MEDIA_CABLE:
    case COMPANY_MODEL.METAL_MINING:
    case COMPANY_MODEL.OIL_AND_GAS:
    case COMPANY_MODEL.PAPER_PACKAGING:
    case COMPANY_MODEL.PIPELINES_MLP:
    case COMPANY_MODEL.RETAIL_GROCERS:
    case COMPANY_MODEL.SHIPPING_INFRASTRUCTURE:
    case COMPANY_MODEL.TECHNOLOGY:
    case COMPANY_MODEL.TELECOMM:
    case COMPANY_MODEL.TRUCKING_RAILS:
    case COMPANY_MODEL.UTILITIES:
    case COMPANY_MODEL.TMT:
      return NON_FINANCIAL_CRL;
    case COMPANY_MODEL.FINANCE:
    case COMPANY_MODEL.BANKS:
    case COMPANY_MODEL.INSURANCE:
    case COMPANY_MODEL.LHINSURANCE:
    case COMPANY_MODEL.PCINSURANCE:
    case COMPANY_MODEL.REITS:
      return FINANCIAL_NON_EURO_CRL;
    default:
      return [];
  }
};
